import { Text } from 'components';
import { useTranslation } from 'react-i18next';
import { TableComponents } from 'screens/Admin/components';
import { titleToLocaleKey } from 'lib';

const { Td, Tr } = TableComponents;

const SYNC_CONFIG = {
  synced: { dot: 'bg-[#27E3A3]', text: '#27E3A3', key: 'subscription.admin.status.synced' },
  pending: { dot: 'bg-[#eab308]', text: '#b59500', key: 'subscription.admin.status.pending' },
  error: { dot: 'bg-[#FA3F58]', text: '#FA3F58', key: 'subscription.admin.status.error' }
};

const ListItem = ({ plan, isSelected, onClick, onSync, syncingId }) => {
  const { t } = useTranslation();
  const monthlyPrice = plan.prices?.find((p) => p.interval === 'monthly');
  const yearlyPrice = plan.prices?.find((p) => p.interval === 'yearly');
  const currency = monthlyPrice?.currency || yearlyPrice?.currency || '';
  const syncStatus = plan.syncStatus?.stripe || 'pending';
  const sync = SYNC_CONFIG[syncStatus] || SYNC_CONFIG.pending;
  const needsSync = syncStatus !== 'synced';
  const isSyncing = syncingId === plan._id;

  return (
    <Tr onClick={() => onClick(plan._id)}>
      <Td first isSelected={isSelected}>
        <div className="flex items-center gap-2">
          {plan.teamId?.avatar && (
            <img
              src={plan.teamId.avatar}
              alt={plan.teamId.name}
              className="w-6 h-6 rounded-full object-cover"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}
          <div className="flex flex-col">
            <Text size="13px" weight="600" col="#353E4A">
              {plan.teamId?.name || t('subscription.admin.unknownTeam')}
            </Text>
            <span className="text-[11px] text-[#97A3B5] flex items-center gap-1">
              {plan.countryCode}
            </span>
          </div>
        </div>
      </Td>
      <Td isSelected={isSelected}>
        <div className="flex items-center gap-2">
          <Text size="13px" col="#20252D">{t(`subscription.plans.${titleToLocaleKey(plan.title)}`, { defaultValue: plan.title })}</Text>
          {plan.badge && (
            <span className="text-[10px] font-bold tracking-wide uppercase px-1.5 py-[2px] rounded bg-[#FFECEE] text-[#FA3F58] whitespace-nowrap">
              {plan.badge === 'Best Value' ? t('subscription.plans.bestValue') : plan.badge}
            </span>
          )}
        </div>
      </Td>
      <Td isSelected={isSelected}>
        <Text size="13px" col="#353E4A">
          {monthlyPrice ? monthlyPrice.amount.toLocaleString() : '—'}
        </Text>
      </Td>
      <Td isSelected={isSelected}>
        <Text size="13px" col="#353E4A">
          {yearlyPrice ? yearlyPrice.amount.toLocaleString() : '—'}
        </Text>
      </Td>
      <Td isSelected={isSelected}>
        <span className="text-[11px] font-bold tracking-widest text-[#7989A0] uppercase">
          {currency}
        </span>
      </Td>
      <Td isSelected={isSelected}>
        <div className="flex items-center gap-1.5">
          <span className={`inline-block w-[7px] h-[7px] rounded-full ${sync.dot}`} />
          <span className="text-[12px] font-medium capitalize" style={{ color: sync.text }}>
            {t(sync.key)}
          </span>
          {needsSync && onSync && (
            <button
              type="button"
              disabled={!!syncingId}
              onClick={(e) => {
                e.stopPropagation();
                onSync(plan._id);
              }}
              className="ml-1 px-2 py-[2px] text-[11px] font-semibold text-[#353E4A] bg-white border border-[#D2D8DF] rounded hover:border-[#B5BDCA] hover:bg-[#F0F2F4] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {isSyncing ? t('subscription.admin.syncingPlan') : t('subscription.admin.syncPlan')}
            </button>
          )}
        </div>
      </Td>
      <Td last isSelected={isSelected}>
        <span
          className={`inline-flex items-center text-[11px] font-semibold tracking-wide px-2.5 py-[3px] rounded-full ${plan.isActive
              ? 'bg-[#e8faf2] text-[#059669]'
              : 'bg-[#F0F2F4] text-[#97A3B5]'
            }`}
        >
          <span className={`w-[5px] h-[5px] rounded-full mr-1.5 ${plan.isActive ? 'bg-[#059669]' : 'bg-[#B5BDCA]'}`} />
          {plan.isActive ? t('subscription.admin.status.active') : t('subscription.admin.status.inactive')}
        </span>
      </Td>
    </Tr>
  );
};

export default ListItem;
